import React, { useEffect, useState } from 'react'
import { Loader2, AlertCircle, Bookmark, RefreshCw } from 'lucide-react'
import { BasecampIcon } from '@/components/icons/basecamp-icon'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useBasecampProjects } from '@/hooks/use-basecamp'
import {
  HomeFeedList,
  HomeFeedRow,
  HomeWidgetShell,
} from '@/components/dashboard/home-widget-shell'
import { HomeFeedContextMenu } from '@/components/dashboard/home-feed-context-menu'
import {
  basecampProjectToSelection,
  homeSelectionKey,
  type HomeContinuityProps,
} from '@/components/dashboard/home-selection'
import { testIds } from '@/lib/test-ids'
import { cn } from '@/lib/utils'
import type { BasecampProject } from '@/types/basecamp'

interface BasecampProjectsSectionProps {
  limit?: number
  continuity?: HomeContinuityProps
}

export function BasecampProjectsSection({
  limit = 6,
  continuity,
}: BasecampProjectsSectionProps) {
  const { projects, isLoading, error, refetch } = useBasecampProjects()
  const [projectsWithStats, setProjectsWithStats] = useState<BasecampProject[]>([])
  const [refreshing, setRefreshing] = useState(false)
  const [contextMenu, setContextMenu] = useState<{
    x: number
    y: number
    project: BasecampProject
  } | null>(null)

  useEffect(() => {
    if (projects && projects.length > 0) {
      const sorted = [...projects].sort((a, b) => {
        if (a.bookmarked && !b.bookmarked) return -1
        if (!a.bookmarked && b.bookmarked) return 1
        return (a.name || '').localeCompare(b.name || '')
      })
      setProjectsWithStats(sorted.slice(0, limit))
    } else {
      setProjectsWithStats([])
    }
  }, [projects, limit])

  useEffect(() => {
    if (!contextMenu) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setContextMenu(null)
    }
    const handleClick = () => setContextMenu(null)
    document.addEventListener('keydown', handleKey)
    document.addEventListener('mousedown', handleClick)
    return () => {
      document.removeEventListener('keydown', handleKey)
      document.removeEventListener('mousedown', handleClick)
    }
  }, [contextMenu])

  const selectProject = (project: BasecampProject, opts?: { openPreview?: boolean }) => {
    continuity?.selectHome(basecampProjectToSelection(project), opts)
  }

  const openProject = (project: BasecampProject) => {
    if (continuity) {
      continuity.openSelection(basecampProjectToSelection(project))
      return
    }
    window.dispatchEvent(
      new CustomEvent('integration-view-change', {
        detail: {
          tabId: `basecamp-project-${project.id}`,
          title: project.name || 'Basecamp Project',
          type: 'integration-basecamp',
          integrationData: {
            integrationId: 'basecamp',
            projectId: project.id.toString(),
          },
        },
      }),
    )
  }

  const openProjectsView = () => {
    window.dispatchEvent(
      new CustomEvent('integration-view-change', {
        detail: {
          tabId: 'basecamp-projects',
          title: 'Projects',
          type: 'integration-basecamp',
          integrationData: {
            integrationId: 'basecamp',
            section: 'projects',
          },
        },
      }),
    )
  }

  const handleRefresh = async () => {
    setRefreshing(true)
    try {
      await refetch()
    } finally {
      setRefreshing(false)
    }
  }

  return (
    <HomeWidgetShell
      title="Basecamp Projects"
      icon={<BasecampIcon className="h-4 w-5" />}
      data-testid={testIds.home.basecampProjects}
      action={
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void handleRefresh()}
            disabled={isLoading || refreshing}
            className="h-7 w-7 p-0"
            title="Refresh projects"
            data-testid={testIds.home.basecampProjectsRefresh}
          >
            <RefreshCw className={cn('h-3.5 w-3.5', (isLoading || refreshing) && 'animate-spin')} />
          </Button>
          {projectsWithStats.length > 0 ? (
            <Button variant="ghost" size="sm" onClick={openProjectsView} className="h-7 px-2">
              View All
            </Button>
          ) : null}
        </div>
      }
    >
      {contextMenu && (
        <HomeFeedContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
          onOpen={() => openProject(contextMenu.project)}
          onPreview={() => selectProject(contextMenu.project, { openPreview: true })}
          workspaces={continuity?.workspaces ?? []}
        />
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      )}

      {error && (
        <Alert variant="destructive" className="bg-destructive/10 border-destructive/50">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-sm font-medium">
            {error instanceof Error ? error.message : 'Failed to load projects'}
          </AlertDescription>
        </Alert>
      )}

      {!isLoading && !error && projectsWithStats.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          <p className="text-sm">No Basecamp projects found</p>
        </div>
      )}

      {!isLoading && !error && projectsWithStats.length > 0 && (
        <HomeFeedList>
          {projectsWithStats.map((project) => {
            const isSelected =
              continuity != null &&
              homeSelectionKey(basecampProjectToSelection(project)) === continuity.selectedKey
            return (
              <HomeFeedRow
                key={project.id}
                title={
                  <span className="inline-flex items-center gap-1.5 min-w-0">
                    <span className="truncate">{project.name}</span>
                    {project.bookmarked ? (
                      <Bookmark className="h-3 w-3 shrink-0 fill-muted-foreground/40 text-muted-foreground" />
                    ) : null}
                  </span>
                }
                meta={project.bookmarked ? 'Bookmarked project' : 'Basecamp project'}
                selected={isSelected}
                onClick={(e) => {
                  if (e.metaKey || e.ctrlKey) {
                    selectProject(project)
                    openProject(project)
                    return
                  }
                  selectProject(project)
                }}
                onDoubleClick={() => {
                  selectProject(project)
                  openProject(project)
                }}
                onContextMenu={(e) => {
                  e.preventDefault()
                  selectProject(project)
                  setContextMenu({ x: e.clientX, y: e.clientY, project })
                }}
              />
            )
          })}
        </HomeFeedList>
      )}
    </HomeWidgetShell>
  )
}
